import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, Star } from 'lucide-react';
import { ProfileActions, StripeActions } from 'store/actions';
import { PortalSpinner } from 'components/PortalSpinner';
import StripeElement from 'components/StripeElement';
import { getPatient } from 'library/auth';
import { MEMBERSHIP_CHANGED } from 'utils/constants';
import { formatPayload } from '../helper';
import CancelMembership from './CancelMembership';
import ChangePlan from './ChangePlan';
import MemberPanel from './MemberPanel';
import MemberPicker from './MemberPicker';
import MembershipEndingNotice from './MembershipEndingNotice';
import MembershipEnded from './MembershipEnded';
import SwitchToMembership from './SwitchToMembership';
import RecoverPayment from './RecoverPayment';
import UnpaidCheckout from './UnpaidCheckout';
import PlanCard from './PlanCard';
import Segmented from './Segmented';
import {
  annualSavings,
  categoryFor,
  coverageLabel,
  discountFor,
  formatMoney,
  hasSellablePlans,
  householdOptions,
  intervalOptions,
  intervalSuffix,
  isLiveEnrollment,
  isPendingCheckout,
  needsMemberPicking,
  planOptionCount,
  priceFor,
  seatLimit,
  visiblePlans,
  HOUSEHOLD_LABELS,
  INTERVAL_LABELS,
  LABEL,
  OUTLINE_PLAN_BTN,
} from './helper';

const enhancer = connect(null, { ...StripeActions, ...ProfileActions });

const Membership = ({
  getMemberships,
  createMembership,
  cancelMembership,
  changeMembershipPlan,
  resumeMembership,
  getMembershipRecovery,
  updateInsurance,
  onStatus = null,
  onAddInsurance = null,
  // false while the host is still asking whether they have insurance: report
  // the status, but don't pitch plans over the top of its own question.
  pitch = true,
  // The insurance form owns this while it's live on screen; standalone, the
  // payload's own flag is the only truth available.
  insured: insuredProp = null,
}) => {
  const [data, setData] = useState(null);
  const [unavailable, setUnavailable] = useState(false);
  const [household, setHousehold] = useState(null);
  const [billingInterval, setBillingInterval] = useState(null);
  const [picking, setPicking] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [exploring, setExploring] = useState(false);
  const [busy, setBusy] = useState(false);

  // Outside the Profile outlet (the appointment modal) this is simply absent.
  const { setPaymentLocked, mergeSection }: any = useOutletContext() || {};

  // Once the enrollment exists the invoice is open, so leaving the step here
  // abandons a real subscription — hold the step's controls until it resolves.
  // `joined` means the payment cleared, so nothing is outstanding any more;
  // `checkout` itself is kept, since the panel still reads the plan off it.
  const paymentPending = !joined && (!!checkout || joining);

  // Two different questions the hosts ask, and conflating them is what put a
  // "you're marked as self-pay" panel above an unpaid checkout:
  //   member   — is the patient actually covered? Drives copy about billing, so
  //              a checkout nobody paid for does not count.
  //   enrolled — is this panel saying something about the patient's own
  //              membership? A pending checkout does count: the host must stand
  //              down rather than contradict it.
  const member = !!joined || (isLiveEnrollment(data?.enrollment) && !isPendingCheckout(data?.enrollment));
  const enrolled = !!joined || isLiveEnrollment(data?.enrollment);

  // Whether this practice sells anything. All three are null until the call
  // lands — a host rendering off a guess flashes the opposite of what it
  // settles on.
  const offering = unavailable ? false : data ? hasSellablePlans(data.plans) : null;
  const known = !unavailable && !!data;
  // A covered family member holds none of this: the API refuses their cancel
  // calls, so the host must not tell them to make one.
  const manageable = data?.enrollment ? data.enrollment.manageable !== false : true;
  const status = {
    offering,
    member: unavailable ? false : known ? member : null,
    enrolled: unavailable ? false : known ? enrolled : null,
    manageable,
    subscriberName: data?.enrollment?.subscriber_name || null
  };

  useEffect(() => {
    loadMemberships();
  }, []);

  useEffect(() => {
    setPaymentLocked?.(paymentPending);

    return () => setPaymentLocked?.(false);
  }, [paymentPending, setPaymentLocked]);

  // No cleanup: React runs it before every re-run too, so resetting to null
  // there would blank the host's copy for a frame on each change.
  useEffect(() => {
    onStatus?.(status);
    // Fields, not `status` — a fresh object every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.offering, status.member, status.enrolled, status.manageable, status.subscriberName, onStatus]);

  // The sidebar link is not a parent, so onStatus can't reach it. Null means
  // the call hasn't landed — announcing that would blank a correct link.
  useEffect(() => {
    if (status.enrolled === null) return;

    window.dispatchEvent(new CustomEvent(MEMBERSHIP_CHANGED, { detail: status.enrolled }));
  }, [status.enrolled]);

  const loadMemberships = async () => {
    try {
      setData(await getMemberships());
    } catch (error) {
      // A practice that hasn't finished connecting Stripe simply has nothing to
      // offer here — not something to interrupt the patient's form with.
      setUnavailable(true);
    }
  };

  if (unavailable) return null;
  if (!data) return <PortalSpinner />;

  const { enrollment, ended_enrollment: ended = null, plans = [], household: people = [] } = data;
  const insured = insuredProp ?? data.insured ?? false;
  // Matched by name for the same reason discountFor does — the enrollment
  // carries no plan id.
  const currentPlan = plans.find(({ name }) => name === enrollment?.plan_name);

  const confirmCancel = async (reason) => {
    setBusy(true);
    try {
      const { enrollment: updated, next_step } = await cancelMembership(enrollment.id, {
        cancellation_reason: reason,
      });

      // Nothing has been cancelled — the practice decides how it ends. The
      // enrollment comes back untouched apart from the request flag, which is
      // what puts the "with the practice" notice on the panel.
      setData((current) => ({ ...current, enrollment: updated }));
      setLeaving(false);
      toast.success('Your cancellation request has been sent to the practice.');
      // A patient leaving for insurance shouldn't be left with no coverage on
      // file — hand them straight to the intake form sitting above this block.
      if (next_step === 'insurance_intake') onAddInsurance?.();
    } catch (error) {
      toast.error(error.errors || 'Could not send your cancellation request. Please try again.');
    }
    setBusy(false);
  };

  // Backing out of a checkout that was never paid for. Nothing was collected, so
  // this ends it outright rather than asking the practice — and it has to, or the
  // patient cannot buy any other plan while the row sits in pending.
  const abandonCheckout = async () => {
    setBusy(true);
    try {
      await cancelMembership(enrollment.id, {});
      setCheckout(null);
      setRecovery(null);
      toast.success('No problem — nothing has been charged.');
    } catch (error) {
      toast.error(error.errors || 'Could not cancel that. Please try again.');
    }
    // Either way the row moved or we are out of step with it.
    await loadMemberships();
    setBusy(false);
  };

  // The open invoice already carries a PaymentIntent, so recovery is the normal
  // checkout pointed at it — paying it is what clears the past-due state.
  const startRecovery = async () => {
    setBusy(true);
    try {
      setRecovery(await getMembershipRecovery(enrollment.id));
    } catch (error) {
      toast.error(error.errors || 'Could not load your payment details. Please try again.');
      // Nothing outstanding means the row is behind Stripe, not that the patient
      // did anything wrong — reloading settles it and shows the real state.
      loadMemberships();
    }
    setBusy(false);
  };

  // Stripe's webhook reactivates the membership, but not before this render —
  // the payment did clear, so show the truth rather than a stale warning.
  const finishRecovery = () => {
    setRecovery(null);
    setData((current) => ({ ...current, enrollment: { ...current.enrollment, state: 'active' } }));
    toast.success('Payment received — your membership is active.');
  };

  // Membership and insurance are alternatives here, not a stack — going back to
  // one takes the other off file. mergeSection keeps the host's cached copy in
  // step so the summary doesn't keep rendering a card that is gone.
  const dropInsurance = async () => {
    const { data } = await updateInsurance(formatPayload({ active: false }));
    mergeSection?.('insurance', data);
  };

  // The switch back: resume billing and remove the insurance that replaced it.
  // Resume first — if it fails there is no reason to touch their coverage.
  const keepMembershipAndDropInsurance = async () => {
    setBusy(true);
    try {
      const { enrollment: updated } = await resumeMembership(enrollment.id);
      await dropInsurance();

      setData((current) => ({ ...current, enrollment: updated }));
      toast.success('Your membership continues — your insurance has been removed.');
    } catch (error) {
      toast.error(error.errors || 'Could not restart your membership. Please try again.');
      // A membership that lapsed while this was on screen 422s here — resync so
      // the panel stops offering a button that can no longer work.
      loadMemberships();
    }
    setBusy(false);
  };

  // Upgrades are charged on the spot, downgrades land at the next renewal —
  // the API decides which from the prices, and the confirmation step already
  // told the patient which one they're getting.
  const applyPlan = async (category_id, memberIds, done) => {
    setBusy(true);
    try {
      const { enrollment: updated } = await changeMembershipPlan(enrollment.id, {
        membership_category_id: category_id,
        ...(memberIds ? { member_ids: memberIds } : {}),
      });

      setData((current) => ({ ...current, enrollment: updated }));
      setSwitching(false);
      toast.success(done);
    } catch (error) {
      toast.error(error.errors || 'Could not change your plan. Please try again.');
      loadMemberships();
    }
    setBusy(false);
  };

  const confirmPlanChange = (category, memberIds) =>
    applyPlan(category.id, memberIds, 'Your membership plan is up to date.');

  // Undoing a scheduled change is the same call, pointed back at the plan they
  // are already on — the API leaves their current coverage alone for it.
  const keepCurrentPlan = () =>
    applyPlan(
      categoryFor(currentPlan, enrollment.household_type).id,
      null,
      `Your ${enrollment.plan_name} plan continues — the change has been called off.`
    );

  const keepMembership = async () => {
    setBusy(true);
    try {
      const { enrollment: updated } = await resumeMembership(enrollment.id);

      setData((current) => ({ ...current, enrollment: updated }));
      toast.success('Your membership will continue.');
    } catch (error) {
      toast.error(error.errors || 'Could not restart your membership. Please try again.');
      loadMemberships();
    }
    setBusy(false);
  };

  if (joined)
    return (
      <MemberPanel
        enrollment={joined}
        justJoined
        discountPercent={checkout?.plan?.treatment_discount_percent ?? null}
      />
    );

  // Started a checkout and never paid: the invoice is open, and the API counts
  // pending as enrolled, so picking another plan just 422s. The only way out is
  // to settle this one — recovery already handles an open invoice whatever the
  // enrollment's state.
  if (isPendingCheckout(enrollment))
    return recovery ? (
      <RecoverPayment
        enrollment={enrollment}
        recovery={recovery}
        unpaid
        onSuccess={finishRecovery}
        onBack={() => setRecovery(null)}
      />
    ) : (
      <UnpaidCheckout
        enrollment={enrollment}
        busy={busy}
        onPay={startRecovery}
        onAbandon={abandonCheckout}
      />
    );

  if (isLiveEnrollment(enrollment)) {
    if (recovery)
      return (
        <RecoverPayment
          enrollment={enrollment}
          recovery={recovery}
          onSuccess={finishRecovery}
          onBack={() => setRecovery(null)}
        />
      );

    if (switching)
      return (
        <ChangePlan
          enrollment={enrollment}
          plans={plans}
          people={people}
          busy={busy}
          onConfirm={confirmPlanChange}
          onBack={() => setSwitching(false)}
        />
      );

    if (leaving)
      return (
        <CancelMembership
          enrollment={enrollment}
          busy={busy}
          onConfirm={confirmCancel}
          onBack={() => setLeaving(false)}
        />
      );

    // Once it is cancelled, when member pricing stops is the headline — whether
    // they moved to insurance or back to self-pay. "You're a member" buries it.
    if (enrollment.cancellation_pending)
      return (
        <MembershipEndingNotice
          enrollment={enrollment}
          insured={insured}
          busy={busy}
          onKeep={insured ? keepMembershipAndDropInsurance : keepMembership}
        />
      );

    return (
      <MemberPanel
        enrollment={enrollment}
        insured={insured}
        discountPercent={discountFor(plans, enrollment)}
        busy={busy}
        onCancel={() => setLeaving(true)}
        onFixPayment={startRecovery}
        // Nothing to move between unless the practice sells more than the one
        // plan they are already on.
        onChangePlan={planOptionCount(plans, enrollment.interval) > 1 ? () => setSwitching(true) : null}
        onKeepPlan={currentPlan ? keepCurrentPlan : null}
      />
    );
  }

  if (!pitch) return null;

  // Ended, with nothing in its place. Shown instead of the grid so the patient
  // finds out their cover stopped — and is offered insurance as well as a plan,
  // rather than only the option that sells one. Insurance already on file means
  // they have chosen; the grid behaves as it did before. Ahead of the sellable
  // check, because a practice that stopped offering plans still owes them the
  // news; they just lose the button.
  if (ended && !insured && !exploring)
    return (
      <MembershipEnded
        enrollment={ended}
        onChoosePlan={hasSellablePlans(plans) ? () => setExploring(true) : null}
        onAddInsurance={onAddInsurance}
      />
    );

  // Not `plans.length` — a tier with every price paused is still in the list,
  // and trusting the count renders the pitch above an empty grid.
  if (!hasSellablePlans(plans)) return null;

  // Someone with coverage on file has already chosen, so the plan grid is not
  // put in front of them — but the door back is. Their membership status, if
  // they have one, rendered above regardless.
  if (insured && !exploring) return <SwitchToMembership onExplore={() => setExploring(true)} />;

  // Derived, not stored: picking a household whose only interval is monthly has
  // to fall back rather than leave an unbuyable combination selected.
  const households = householdOptions(plans);
  const activeHousehold = households.some(({ key }) => key === household) ? household : households[0]?.key;

  const intervals = intervalOptions(plans, activeHousehold);
  const activeInterval = intervals.some(({ key }) => key === billingInterval)
    ? billingInterval
    : intervals[0]?.key;

  const subscriberId = people.find(({ is_subscriber }) => is_subscriber)?.id;

  // A single plan has nothing to choose, so it goes straight to payment. Couple
  // and family stop at the picker first — no subscription is minted until the
  // patient has actually named who it covers.
  const handleChoose = (plan) => {
    if (!needsMemberPicking(activeHousehold)) return startCheckout(plan);

    setPicking({ plan, selectedIds: subscriberId ? [subscriberId] : [] });
  };

  const toggleMember = (id) =>
    setPicking((current) => ({
      ...current,
      selectedIds: current.selectedIds.includes(id)
        ? current.selectedIds.filter((selected) => selected !== id)
        : [...current.selectedIds, id],
    }));

  const startCheckout = async (plan, memberIds = null) => {
    setJoining(true);
    try {
      const { enrollment: pending, client_secret } = await createMembership({
        membership_category_id: categoryFor(plan, activeHousehold).id,
        interval: activeInterval,
        ...(memberIds ? { member_ids: memberIds } : {}),
      });

      setPicking(null);
      setCheckout({ plan, enrollment: pending, clientSecret: client_secret });
    } catch (error) {
      toast.error(error.errors || 'Could not start your membership. Please try again.');
      // A membership bought elsewhere 422s here — resync so the panel shows it.
      loadMemberships();
    }
    setJoining(false);
  };

  if (picking) {
    const { plan, selectedIds } = picking;

    return (
      <MemberPicker
        plan={plan}
        household={activeHousehold}
        people={people}
        limit={seatLimit(plan, activeHousehold)}
        selectedIds={selectedIds}
        busy={joining}
        onToggle={toggleMember}
        onBack={() => setPicking(null)}
        onContinue={() => startCheckout(plan, selectedIds)}
      />
    );
  }

  if (checkout) {
    const { plan, clientSecret } = checkout;
    // getPractice() throws on a missing patient rather than returning undefined
    const practiceName = getPatient()?.practice?.data?.attributes?.title || 'this practice';
    const price = formatMoney(priceFor(plan, activeHousehold, activeInterval));

    return (
      <div className="animate-slide-up">
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-secondary-200 bg-secondary-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className={LABEL}>Your membership</p>
            <p className="mt-1 text-base font-bold text-secondary-900">
              {plan.name} · {INTERVAL_LABELS[activeInterval]} · {HOUSEHOLD_LABELS[activeHousehold]}
            </p>
            <p className="mt-0.5 text-xs text-secondary-400">{coverageLabel(activeInterval)}</p>
          </div>

          <p className="shrink-0 text-2xl font-extrabold text-secondary-900 sm:text-right">
            {price}{' '}
            <span className="text-sm font-medium text-secondary-400">{intervalSuffix(activeInterval)}</span>
          </p>
        </div>

        {/* the secret is minted once, on the click above — never in an effect */}
        <StripeElement
          clientSecret={clientSecret}
          onSuccess={() => {
            setJoined(checkout.enrollment);
            // Disclosed on the way in — the membership replaces the plan.
            if (insured) dropInsurance();
          }}
        />

        <div className="mt-2 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setCheckout(null)}
            className={`${OUTLINE_PLAN_BTN} shrink-0 gap-2 px-4`}
          >
            <ArrowLeft size={15} />
            Choose a different plan
          </button>

          <p className="text-[11px] leading-relaxed text-secondary-400 sm:text-right">
            By joining you authorise {practiceName} to charge {price}{' '}
            {intervalSuffix(activeInterval)} to this card. Renews automatically — cancel anytime.
            {insured && ' Your insurance will be taken off file once this payment clears.'}
          </p>
        </div>
      </div>
    );
  }

  const savings = activeInterval === 'year' ? annualSavings(plans, activeHousehold) : null;

  return (
    <div className="animate-slide-up">
      <div className="mb-6 flex items-start gap-4 rounded-xl border border-gray-200 bg-secondary-50/60 p-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-teamcare-blue">
          <Star size={20} />
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-bold text-secondary-900">
            {insured ? 'Switching to a membership' : 'No insurance? Become a member and save.'}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-secondary-500">
            {insured
              ? 'Pick a plan below. Your insurance stays on file until the payment clears, then comes off — you can cancel the membership anytime.'
              : 'No insurance needed, no waiting periods. Discounts start the moment you join — and you can cancel anytime.'}
          </p>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        {households.length > 1 && (
          <div className="min-w-0 flex-1">
            <p className={LABEL}>Who's joining</p>
            <div className="mt-2">
              <Segmented options={households} value={activeHousehold} onChange={setHousehold} fill />
            </div>
          </div>
        )}

        {intervals.length > 1 && (
          <div className="min-w-0">
            <p className={LABEL}>Billing</p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Segmented options={intervals} value={activeInterval} onChange={setBillingInterval} />
              {savings && (
                <span className="rounded-full bg-success-50 px-3 py-1.5 text-xs font-semibold text-success-700">
                  Save {formatMoney(savings)} vs monthly
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 pt-1 sm:grid-cols-2 lg:grid-cols-3">
        {visiblePlans(plans, activeHousehold, activeInterval).map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            household={activeHousehold}
            interval={activeInterval}
            disabled={joining}
            onChoose={handleChoose}
          />
        ))}
      </div>

      {/* The grid is reached from a screen that offered something else too, so
          there has to be a way back to it — a patient who doesn't like the
          plans is otherwise stranded on them. */}
      {(insured || ended) && (
        <button
          type="button"
          onClick={() => setExploring(false)}
          className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-400 underline underline-offset-2 hover:text-secondary-600"
        >
          <ArrowLeft size={13} strokeWidth={2.5} />
          {insured ? 'Never mind — keep my insurance' : 'Back to my options'}
        </button>
      )}

      <p className="mt-4 text-[11px] leading-relaxed text-secondary-400">
        Billed to the card you enter at checkout. {coverageLabel(activeInterval)} and renews
        automatically — cancel anytime before renewal.
      </p>
    </div>
  );
};

export default enhancer(Membership);
